import { UniqueIdentifier } from '@dnd-kit/core';
import Brightness from '@mui/icons-material/Brightness1';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, fontWeight } from '../../../../theme';
import ColorPickerModal from '../../../Modals/ColorPickerModal/colorPickerModal';
import Action from '../Item/components/Action/Action';
import Handle from '../Item/components/Handle';
import Remove from '../Item/components/Remove';
import styles from './TreeItem.module.scss';

interface TreeItemProps{
    id: UniqueIdentifier;
    childCount?: number;
    clone?: boolean;
    collapsed?: boolean;
    depth: number;
    disableInteraction?: boolean;
    disableSelection?: boolean;
    fillColor?: string;
    ghost?: boolean;
    handleProps?: any;
    indicator?: boolean;
    indentationWidth: number;
    style: any;
    value: string;
    isCrossing?: boolean;
    isGroupale?: boolean;
    onCollapse?(): void;
    onRemove?(): void;
    wrapperRef?(node: HTMLLIElement): void;
    itemBuilder(id: UniqueIdentifier, isChildren: number, isClone: boolean):React.ReactNode;
    handleChangeFillColor(id: UniqueIdentifier, color?: string): void;
}

const TreeItem = React.forwardRef<HTMLDivElement, TreeItemProps>(
    (
        {
            id,
            childCount,
            clone,
            depth,
            disableSelection,
            disableInteraction,
            fillColor,
            ghost,
            handleProps,
            isCrossing,
            isGroupale,
            indentationWidth,
            indicator,
            collapsed,
            onCollapse,
            onRemove,
            style,
            wrapperRef,
            itemBuilder,
            handleChangeFillColor,
            ...props
        },
        ref,
    ) => {
        const [openColorPickerModal, setOpenColorPickerModal] = useState<boolean>(false);
        const { t } = useTranslation('dashboard');
        const handleClickDisplayColorPicker = () => {
            setOpenColorPickerModal(true);
        };

        return (
            <li
                ref={wrapperRef}
                className={classNames(
                    styles.Wrapper,
                    clone && styles.clone,
                    ghost && styles.ghost,
                    indicator && styles.indicator,
                    disableSelection && styles.disableSelection,
                    disableInteraction && styles.disableInteraction,
                )}
                style={
            {
                '--spacing': `${indentationWidth * depth}px`,
            } as React.CSSProperties
                }
                {...props}
            >
                <div
                    ref={ref}
                    className={styles.TreeItem}
                    style={isGroupale === false && depth > 0 ? { ...style, backgroundColor: colors.grey450 } : style}
                >
                    {isGroupale === false ? (
                        <Tooltip
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: colors.grey100,
                                        borderRadius: '4px',
                                        color: colors.grey000,
                                        fontSize: fontSize.sm,
                                        fontWeight: fontWeight.semiRegular,
                                        maxWidth: 360,
                                        padding: '6px 10px',
                                        '& .MuiTooltip-arrow': { color: colors.grey100 },
                                    },
                                },
                            }}
                            placement="top"
                            title={t('addIndicatorModal.refineIndicatorAnswer.dragHandleTooltip')}
                            arrow
                        >
                            <span>
                                <Handle {...handleProps} />
                            </span>
                        </Tooltip>
                    ) : (
                        <Handle {...handleProps} />
                    )}
                    {!isCrossing && !clone && (
                        <>
                            <Brightness onClick={handleClickDisplayColorPicker} sx={{ color: fillColor, cursor: 'pointer' }} />
                            <ColorPickerModal codemodalite={id} fillColor={fillColor} handleChangeFillColor={handleChangeFillColor} openModal={openColorPickerModal} setOpenModal={setOpenColorPickerModal} />
                        </>
                    )}
                    {onCollapse && (
                        <Action
                            className={classNames(
                                styles.Collapse,
                                collapsed && styles.collapsed,
                            )}
                            onClick={onCollapse}
                        >
                            T
                        </Action>
                    )}
                    {itemBuilder(id, indentationWidth * depth, clone ?? false)}
                    {!clone && onRemove && <Remove onClick={onRemove} />}
                    {clone && childCount && childCount > 1 ? (
                        <span className={styles.Count}>{childCount}</span>
                    ) : null}

                </div>
            </li>
        );
    },
);

export default TreeItem;
